import {
    type ChangeEvent,
    type FC,
    type MouseEvent,
    type ReactNode,
    type SetStateAction,
    useEffect,
    useRef,
    useState,
} from "react";
import { Select, type SelectOptionType } from "@onshopfront/components";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import Button from "../../Buttons/Button";
import ClearableInput from "../../Forms/ClearableInput";
import { File } from "./File";
import { FileGrid } from "./FileGrid";
import {
    getFile,
    type MediaDirectoryType,
    type MediaFilePages,
    MediaItemType,
    type MediaPathType,
    type MediaSelectedType,
} from "./MediaUtilities";

interface Props {
    small?: boolean;
    loading: boolean;
    directory: MediaDirectoryType | null;
    items: MediaFilePages;
    current: Array<MediaPathType>;
    onLoadMore: (index: number) => Promise<void>;
    onDirChange: (path: Array<MediaPathType>) => void;
    onChange: (value: SetStateAction<Array<MediaSelectedType>>) => void;
    onSelect: (items: Array<MediaSelectedType>) => void;
    selected: Array<MediaSelectedType>;
    onReorder: (order: FileOrderType) => void;
    order: FileOrderType;
    onRefresh: () => Promise<void>;
    onNewFolder: (e: MouseEvent) => void;
    onSearch: (search: string) => void;
    search: string;
    multi: boolean;
}

export interface FileOrderType {
    dir: "ASC" | "DESC";
    value: SelectOptionType<string>;
}

type DoubleClickType = null | MediaSelectedType;

export const SORT_OPTIONS = [{
    label: "Created At",
    value: "created_at",
}, {
    label: "Last Modified",
    value: "last_modified",
}, {
    label: "Name",
    value: "name",
}, {
    label: "File Type",
    value: "file_type",
}];

type ChangeMode = "replace" | "toggle" | "between";

export const FileViewer: FC<Props> = ({
    small,
    loading,
    directory,
    items,
    current,
    onLoadMore,
    onDirChange,
    onChange,
    onSelect,
    selected,
    onSearch,
    onReorder,
    onRefresh,
    onNewFolder,
    order,
    search,
    multi,
}) => {
    const [ refreshing, setRefreshing ] = useState(false);

    const pathRef = useRef<HTMLDivElement>(null);
    const lastClickedRef = useRef<DoubleClickType>(null);
    const clickTimerRef = useRef<null | ReturnType<typeof setTimeout>>(null);

    useEffect(() => {
        if(pathRef.current) {
            pathRef.current.scrollLeft = pathRef.current.scrollWidth;
        }

        setRefreshing(false);
    }, [ current ]);

    const onChangeHandle = (item: MediaSelectedType, type: ChangeMode): void => {
        const hasFolder = selected.find(i => i.type === MediaItemType.Folder) !== undefined;

        if(type === "replace" || hasFolder) {
            onChange([ item ]);
        } else if(type === "toggle") {
            onChange(prevState => {
                const index = prevState.findIndex(i => i.id === item.id);

                if(index < 0) {
                    return [
                        ...prevState,
                        item,
                    ];
                }

                return [
                    ...prevState.slice(0, index),
                    ...prevState.slice(index + 1),
                ];
            });
        } else if(type === "between") {
            const existing = new Set<string>();

            let closestDistance: null | number = null;
            let closest: null | MediaSelectedType = null;

            for(const i of selected) {
                existing.add(i.id);

                const distance = Math.abs(i.index - item.index);

                if(closestDistance === null || distance < closestDistance) {
                    closestDistance = distance;
                    closest = i;
                } else if(distance === closestDistance) {
                    closestDistance = distance;

                    if(!closest) {
                        closest = i;
                    } else if(i.index < closest.index) {
                        closest = i;
                    }
                }
            }

            if(!closest) {
                return;
            }

            const results: Array<MediaSelectedType> = [];

            const start = Math.min(closest.index, item.index);
            const end = Math.max(closest.index, item.index);

            for(let i = start; i <= end; i++) {
                const item = getFile(i, items);

                if(!item) {
                    continue;
                }

                if(existing.has(item.uuid)) {
                    continue;
                }

                results.push({
                    type : MediaItemType.File,
                    id   : item.uuid,
                    name : item.name,
                    index: i,
                });
            }

            onChange(prevState => [
                ...prevState,
                ...results,
            ]);
        }
    };

    const onClick = (e: MouseEvent, index: number, item: MediaSelectedType): void => {
        e.preventDefault();

        if(
            !(lastClickedRef.current?.type === item.type && lastClickedRef.current?.id === item.id) &&
            clickTimerRef.current !== null
        ) {
            clearTimeout(clickTimerRef.current);
            clickTimerRef.current = null;
        }

        // Double click handler (done this way to work with touch devices)
        if(clickTimerRef.current === null) {
            if(index >= 0) {
                if(item.type === MediaItemType.File) {
                    let mode: ChangeMode = "replace";

                    if(e.ctrlKey || e.metaKey) {
                        mode = "toggle";
                    } else if(e.shiftKey) {
                        mode = "between";
                    }

                    onChangeHandle(item, mode);
                } else if(item.type === MediaItemType.Folder) {
                    onChange([ item ]);
                }
            }

            lastClickedRef.current = item;

            clickTimerRef.current = setTimeout(() => {
                clickTimerRef.current = null;
                lastClickedRef.current = null;
            }, 500);
        } else {
            clearTimeout(clickTimerRef.current);
            clickTimerRef.current = null;
            lastClickedRef.current = null;

            onDoubleClick(e, item);
        }
    };

    const onDoubleClick = (e: MouseEvent, item: MediaSelectedType): void => {
        if(item.type === MediaItemType.Folder) {
            if(item.id === "new-folder") {
                onNewFolder(e);

                return;
            }

            const dir = [ ...current ];

            if(item.id === "...") {
                dir.pop();
            } else {
                dir.push({
                    id  : item.id,
                    name: item.name,
                });
            }

            onDirChange(dir);
        } else if(item.type === MediaItemType.File) {
            onSelect([ item ]);
        }
    };

    const onToggleSelect = (item: MediaSelectedType): void => {
        onChangeHandle(item, "toggle");
    };

    const onWrapperClick = (e: MouseEvent<HTMLDivElement>): void => {
        if(!(e.target as HTMLElement).closest(".file")) {
            onChange([]);
        }
    };

    const onSearchChange = (e: ChangeEvent<HTMLInputElement>): void => {
        onSearch(e.currentTarget.value);
    };

    const onSearchClear = (): void => {
        onSearch("");
    };

    const onRefreshClick = () => {
        setRefreshing(true);

        onRefresh()
            .then(() => {
                setRefreshing(false);
            });
    };

    const onOrderChange = (value: SelectOptionType<string>): void => {
        onReorder({
            ...order,
            value,
        });
    };

    const onOrderToggle = (): void => {
        onReorder({
            ...order,
            dir: order.dir === "DESC" ? "ASC" : "DESC",
        });
    };

    let files;

    if(loading) {
        files = (
            <div className="files-loading">
                <FontAwesomeLight icon="spinner" className="fa-pulse" />
            </div>
        );
    } else if(!directory) {
        files = (
            <div className="files-none">
                <FontAwesomeLight icon="exclamation-triangle" />
                <div className="mt-4">
                    Failed to load the directory
                </div>
                <File
                    index={-1}
                    id="..."
                    name="..."
                    type={MediaItemType.Folder}
                    onClick={onClick}
                    selected={false}
                    navUp
                    connections={[]}
                />
            </div>
        );
    } else if(directory.folders.length === 0 && directory.fileCount === 0) {
        files = (
            <div className="files-none">
                {!!current.length && (
                    <File
                        index={-1}
                        id="..."
                        name="..."
                        type={MediaItemType.Folder}
                        onClick={onClick}
                        selected={false}
                        navUp
                        connections={[]}
                    />
                )}
                No files found
            </div>
        );
    } else {
        files = (
            <FileGrid
                current={current}
                directory={directory}
                items={items}
                onWrapperClick={onWrapperClick}
                onLoadMore={onLoadMore}
                onClick={onClick}
                selected={selected}
                small={small}
                multi={multi}
                onToggle={onToggleSelect}
            />
        );
    }

    let header: ReactNode = null;

    if(!small) {
        header = (
            <div className="file-viewer-header">
                <div
                    className="directory-path"
                    ref={pathRef}
                >
                    {!current.length ? (
                        <div className="path-item">
                            <span className="separator">/</span>
                        </div>
                    ) : (
                        current.map((dir, index) => (
                            <div key={dir.id} className="path-item">
                                <span className="separator">/</span>
                                <div
                                    className="dir"
                                    onClick={(e): void => {
                                        e.stopPropagation();
                                        onDirChange(current.slice(0, index + 1));
                                    }}
                                >
                                    {dir.name}
                                </div>
                            </div>
                        ))
                    )}
                </div>
                <div className="file-viewer-actions">
                    <Button
                        type="button"
                        classes={[ "file-viewer-action" ]}
                        onClick={onRefreshClick}
                    >
                        <FontAwesomeLight icon="sync" spin={refreshing} />
                    </Button>
                    <Button
                        type="button"
                        classes={[ "file-viewer-action" ]}
                        onClick={onOrderToggle}
                    >
                        <FontAwesomeLight
                            icon={(order.dir === "DESC" ? "sort-amount-down" : "sort-amount-up")}
                        />
                    </Button>
                    <Select
                        options={SORT_OPTIONS}
                        value={order.value}
                        className="file-view-sort-select"
                        onChange={onOrderChange}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="file-viewer">
            {header}
            <div className="search">
                <ClearableInput
                    type="text"
                    placeholder="Search for file in current directory"
                    onChange={onSearchChange}
                    onClear={onSearchClear}
                    value={search}
                />
            </div>
            {files}
        </div>
    );
};
